// Core imports
import React, { Fragment, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { t } from 'i18next';
import { useParams } from 'react-router-dom';

// Component Imports
import { Card, Col, Row } from 'antd';
import DecisionTreesUsageSection from './DecisionTreesUsageSection';
import ActivityFunnelingTable from './ActivityFunnelingTable';

// Misc imports
import { ANALYTICS_SECTIONS } from '../../constants/main';
import { v4 as uuidv4 } from 'uuid';
import { useFetchDecisionTreesAnalyticsQuery } from '../../store/analytics/analyticsApi';
import { useFetchBotsQuery } from '../../store/bots/botsApi';
import { isEmptyArray } from '../../utils/is/isEmptyArray';

const AnalyticsDecisionTreesTab = ({
  requestParams,
  isPublicMode,
  setIsLoading = () => false
}) => {
  const { orgId } = useParams();
  const { data: bots } = useFetchBotsQuery({ orgId, urlParams: { includeBotDeployments: true } }, { skip: !orgId || isPublicMode });

  const { data: decisionTreesAnalytics, isFetching: isFetchingDecisionTreesAnalytics, isUninitialized } = useFetchDecisionTreesAnalyticsQuery({ ...requestParams?.[0] }, { skip: isEmptyArray(requestParams) });

  // Memoize loading state to prevent unnecessary re-renders
  const isLoading = useMemo(() =>
    isFetchingDecisionTreesAnalytics || isUninitialized,
  [isFetchingDecisionTreesAnalytics, isUninitialized]
  );

  useEffect(() => {
    setIsLoading(isLoading);

    return () => {
      setIsLoading(false);
    };
  }, [isLoading]);

  const renderDecisionTreesSection = () => {
    const currentTenantsIncluded = new Set(decisionTreesAnalytics?.activityFunnel?.map((item) => item.tenantId));

    const activeBots = bots?.items?.map((bot) => {
      return {
        [bot.id]: bot.displayName
      };
    }).filter((bot) => currentTenantsIncluded.has(Object.keys(bot)[0]));

    const activityFunnelingTransformedData = decisionTreesAnalytics?.activityFunnel?.map((item) => {
      const tenantDisplayName = activeBots?.find((bot) => Object.keys(bot)[0] === item.tenantId);

      return {
        ...item,
        tenantDisplayName: tenantDisplayName ? Object.values(tenantDisplayName)[0] : '',
        uniqueKey: `decision-trees***${item.activityId}${item.tenantId}***${uuidv4()}`
      };
    });

    return (
      <Fragment>
        <Row gutter={[16, 16]} className="mb-5 hide-empty" id={`analytics-section_${ANALYTICS_SECTIONS.DECISION_TREES.key}`}>
          <Col xs={24} md={12} xxl={12} id={`analytics-section_${ANALYTICS_SECTIONS.DECISION_TREES.key}_${ANALYTICS_SECTIONS.DECISION_TREES.subSections.USAGE.key}`}>
            <Card className="h-100">
              <DecisionTreesUsageSection
                data={decisionTreesAnalytics?.usage}
                isLoading={isLoading}
                seriesName={t('Decision Tree')}
                sectionTitle={t('Usage')}
              />
            </Card>
          </Col>

          {
            !isPublicMode ? (
              <Col xs={24} md={12} xxl={12} id={`analytics-section_${ANALYTICS_SECTIONS.DECISION_TREES.key}_${ANALYTICS_SECTIONS.DECISION_TREES.subSections.ACTIVITY_FUNNELING.key}`}>
                <Card className="h-100">
                  <ActivityFunnelingTable
                    isLoading={isLoading}
                    activityFunnelingData={activityFunnelingTransformedData?.filter((activity) => activity.activityDisplayName !== null)}
                  />
                </Card>
              </Col>
            ) : null
          }
        </Row>
      </Fragment>
    );
  };

  return (
    <Fragment>
      { renderDecisionTreesSection() }
    </Fragment>
  );
};

AnalyticsDecisionTreesTab.propTypes = {
  requestParams: PropTypes.array,
  isPublicMode: PropTypes.bool,
  setIsLoading: PropTypes.func
};

export default AnalyticsDecisionTreesTab;
